import React, { useRef, useCallback } from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import { NodeViewProps } from '@tiptap/core';
import { ImagePlus, Upload } from 'lucide-react';

const ImagePlaceholderNodeView: React.FC<NodeViewProps> = ({
  node,
  deleteNode,
  editor,
  getPos,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { text } = node.attrs;
  const isEditable = editor.isEditable;

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const imageUrl = e.target?.result as string;
      
      const pos = getPos();
      if (pos === undefined) {
        return;
      }
      editor.commands.insertContentAt(pos, {
        type: 'paragraph',
        attrs: { textAlign: 'center' },
        content: [
          {
            type: 'customImage',
            attrs: {
              src: imageUrl,
              alt: file.name,
              width: 400,
              height: 300,
              'data-image-id': `image-${Date.now()}`,
            },
          }
        ]
      });
      
      // Delete the placeholder after a small delay to ensure insertion is complete
      setTimeout(() => {
        try {
          deleteNode();
        } catch (error) {
          console.error('Error deleting placeholder:', error);
          // Fallback: try to delete by position
          const currentPos = getPos();
          if (currentPos !== undefined) {
            editor.commands.deleteRange({ from: currentPos, to: currentPos + 1 });
          }
        }
      }, 10);
    };
    
    reader.readAsDataURL(file);
  }, [editor, deleteNode, getPos]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const files = Array.from(e.dataTransfer.files);
    const imageFile = files.find(file => file.type.startsWith('image/'));
    
    if (!imageFile) {
      alert('Please drop an image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const imageUrl = event.target?.result as string;
      
      const pos = getPos();
      if (pos === undefined) {
        return;
      }
      
      editor.commands.insertContentAt(pos, {
        type: 'paragraph',
        attrs: { textAlign: 'center' },
        content: [
          {
            type: 'customImage',
            attrs: {
              src: imageUrl,
              alt: imageFile.name,
              width: 400,
              height: 300,
              'data-image-id': `image-${Date.now()}`,
            },
          }
        ]
      });
      
      // Delete the placeholder after a small delay to ensure insertion is complete
      setTimeout(() => {
        try {
          deleteNode();
        } catch (error) {
          console.error('Error deleting placeholder (drag):', error);
          // Fallback: try to delete by position
          const currentPos = getPos();
          if (currentPos !== undefined) {
            editor.commands.deleteRange({ from: currentPos, to: currentPos + 1 });
          }
        }
      }, 10);
    };
    
    reader.readAsDataURL(imageFile);
  }, [editor, deleteNode, getPos]);

  if (!isEditable) {
    return (
      <NodeViewWrapper style={{ margin: '1rem 0' }}>
        <div
          style={{
            minHeight: '120px',
            padding: '1rem',
            textAlign: 'center',
            color: '#9ca3af',
            fontSize: '0.875rem',
          }}
          aria-hidden
        />
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper style={{ margin: '1rem 0' }}>
      <div
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        style={{
          border: '2px dashed #6b7280',
          borderRadius: '12px',
          padding: '3rem 2rem',
          textAlign: 'center',
          cursor: 'pointer',
          backgroundColor: 'rgba(107, 114, 128, 0.05)',
          transition: 'all 0.2s ease',
          minHeight: '200px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '1rem',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = '#3b82f6';
          e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.05)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = '#6b7280';
          e.currentTarget.style.backgroundColor = 'rgba(107, 114, 128, 0.05)';
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
          <div style={{ 
            display: 'flex', 
            gap: '0.5rem',
            color: '#6b7280',
            fontSize: '0.875rem',
            fontWeight: '500'
          }}>
            <ImagePlus size={20} />
            <Upload size={20} />
          </div>
          
          <div style={{ 
            color: '#6b7280',
            fontSize: '1rem',
            fontWeight: '500'
          }}>
            {text}
          </div>
          
          <div style={{ 
            color: '#9ca3af',
            fontSize: '0.875rem'
          }}>
            Click to browse or drag and drop
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
      </div>
    </NodeViewWrapper>
  );
};

export default ImagePlaceholderNodeView;
